
import { PUBLISH_VERSION_MODAL_KEY } from '../constants';
import Vue from 'vue';
import { restApi } from '@/components/mixins/restApi';
import { showMessage } from '@/components/mixins/showMessage';
import mixins from 'vue-typed-mixins';
import { mapGetters } from 'vuex';

import Modal from './Modal.vue';
import AliareInput from '@/components/AliareInput.vue';
import ModalButtonSuccess from '@/components/ModalButtonSuccess.vue';
import ModalButtonCancel from '@/components/ModalButtonCancel.vue';
import AlertNotificationModal from '@/components/AlertNotificationModal.vue';
import { N8nSpinner } from 'n8n-design-system';

export default mixins(
	restApi,
	showMessage,
).extend({
	name: 'PublishVersionModal',
	components: {
		Modal,
        AliareInput,
		ModalButtonSuccess,
		ModalButtonCancel,
		AlertNotificationModal,
		N8nSpinner,
	},
	props: {
		dataModal: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			modalBus: new Vue(),
			PUBLISH_VERSION_MODAL_KEY,
			versionName: '',
			description: '',
			previousVersions: [] as any[],
			isLoading: false,
			currentVersionId: '' as string,
			showOverwriteAlert: false,
		};
	},
	computed: {
        ...mapGetters({
            workspace: 'workspace',
			typeExecutionWorkflow: 'isActive',
			workflowSettings: 'workflowSettings',
			hasTrigger: 'hasTriggerNode',
		}),
		versionToPublish(): string | null {
			return this.$store.state.versionToPublish;
		},
		workflowIdToPublish(): string | null {
			return this.$store.state.workflowIdToPublish;
		},
		workflowId(): string {
            return this.workflowIdToPublish || this.$route.params.name || this.$route.params.workflowid || this.$store.getters.workflowId || this.$store.state.workflow?.id || '';
        },
		shouldRedirectAfterPublish(): boolean {
			return this.dataModal?.skipRedirectAfterPublish !== true;
		},
		isFormValid(): boolean {
			return this.versionName.trim().length > 0 && this.description.trim().length > 0;
		},
	},
	watch: {
		workflowId: {
			immediate: true,
			handler(value: string) {
				if (value) {
					this.loadVersions();
				}
			},
		},
	},
	methods: {
		async loadVersions() {
			if (!this.workflowId) {
				return;
			}

			this.isLoading = true;
			try {
				const response = await this.restApi().getWorkflowVersion(this.workspace.id, this.workflowId);
				
				if (!response || response.length === 0) return;
				
				// Filtrar versões publicadas
				this.previousVersions = response.filter((v: any) => v.nomePublicacao);
				
				// Verificar se há uma versão específica para publicar (vinda do histórico)
				if (this.versionToPublish) {
					this.currentVersionId = this.versionToPublish;
				} else {
					// Pegar a última versão não publicada (versão de trabalho atual - mais recente)
					const unpublishedVersions = response.filter((v: any) => !v.publicado);
					if (unpublishedVersions.length > 0) {
						this.currentVersionId = unpublishedVersions[unpublishedVersions.length - 1].id;
					}
				}
			} catch (error: any) {
				console.error('Erro ao carregar versões:', error);
				this.$store.commit('activeAlert', {
					message: error?.notifications?.[0],
					status: 'error',
				});
			} finally {
				this.isLoading = false;
			}
		},

		async publishVersion() {
			if (!this.isFormValid) return;

			if (!this.currentVersionId) {
				this.$store.commit('activeAlert', {
					message: 'Não foi possível identificar a versão atual',
					status: 'error',
				});
				return;
			}

			// Verificar se já existe uma versão com o mesmo nome
			const versionExists = this.previousVersions.some(
				(v: any) => v.nomePublicacao?.toLowerCase() === this.versionName.trim().toLowerCase()
			);

			if (versionExists) {
				// Mostrar modal de confirmação
				this.showOverwriteAlert = true;
				return;
			}

			// Se não existe duplicata, prosseguir com a publicação
			await this.confirmPublishVersion();
		},

		async confirmPublishVersion() {
			this.showOverwriteAlert = false;
			this.isLoading = true;

			try {
				// Chamar API para publicar versão
				await this.restApi().publishWorkflowVersion(
					this.workspace.id,
					this.workflowId,
					this.currentVersionId, {
                        nomeVersao: this.versionName,
                        descricaoVersao: this.description 
                    }
				);

				this.$store.commit('activeAlert', {
					message: 'Versão publicada com sucesso',
				});

				// Resetar flag de alterações não publicadas
				this.$store.commit('setHasUnpublishedChanges', false);
				this.$store.commit('setStateDirty', false);

				this.closeModal();
                // Emitir evento para atualizar dados se necessário
                this.$root.$emit('versionPublished');

				if (!this.shouldRedirectAfterPublish) {
					return;
				}

                // Redirecionar para a versão publicada
                const targetRoute = this.$router.resolve({
                    name: 'NodeViewExisting',
					params: { name: this.workflowId, mode: 'Editar' },
                });
                const currentRoute = this.$route;
                const isSameRoute =
                    currentRoute.name === 'NodeViewExisting' &&
                 	   String(currentRoute.params.name) === String(this.workflowId);

                if (isSameRoute) {
                    window.location.reload();
                } else {
                    this.$router.push(targetRoute.location);
                }

            } catch (error) {
				console.error('Erro ao publicar versão:', error);
				this.$store.commit('activeAlert', {
					message: 'Erro ao publicar versão: ' + error?.notifications,
					status: 'error',
				});
			} finally {
				this.isLoading = false;
			}
		},

		cancelOverwrite() {
			this.showOverwriteAlert = false;
		},

		closeModal() {
			this.versionName = '';
			this.description = '';
			// Limpar versionToPublish do store
			this.$store.commit('setVersionToPublish', null);
			this.$store.commit('setWorkflowIdToPublish', null);
			this.$store.commit('ui/setDataModal', { name: PUBLISH_VERSION_MODAL_KEY, data: {} }, { root: true });
			this.modalBus.$emit('close');
		},
	},
});
